import React, { useState } from 'react';
import '../shared.css';
import './EditableDescription.css';

interface EditableDescriptionProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  placeholder?: string;
  disabled?: boolean;
}

const EditableDescription: React.FC<EditableDescriptionProps> = ({
  value,
  onSave,
  placeholder = 'Click to add description',
  disabled = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);

  const handleEdit = () => {
    if (!disabled) {
      setEditValue(value);
      setIsEditing(true);
    }
  };

  const handleSave = async () => {
    if (editValue.trim() !== value) {
      setIsSaving(true);
      try {
        await onSave(editValue.trim());
        setIsEditing(false);
      } catch (err) {
        console.error('Error saving description:', err);
      } finally {
        setIsSaving(false);
      }
    } else {
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
    // Ctrl/Cmd + Enter to save
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      handleSave();
    }
  };

  if (isEditing) {
    return (
      <div className="editable-description editing">
        <textarea
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="description-textarea"
          autoFocus
          rows={3}
          disabled={isSaving}
        />
        <div className="description-actions">
          <button
            className="tab-btn tab-btn--ghost"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="tab-btn tab-btn--primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
        <small className="description-hint">
          Press Esc to cancel, Ctrl+Enter to save
        </small>
      </div>
    );
  }

  return (
    <div
      className={`editable-description ${value ? 'has-value' : 'no-value'} ${disabled ? 'disabled' : ''}`}
      onClick={handleEdit}
      title={disabled ? '' : 'Click to edit description'}
    >
      {value || <span className="placeholder">{placeholder}</span>}
      {!disabled && <span className="edit-icon">✏️</span>}
    </div>
  );
};

export default EditableDescription;
